import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://agents.matix.one/api/v1',
  timeout: 30000
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('helpdesk_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const RequestService = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [contract, setContract] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    equipment_id: '',
    description: '',
    priority: 'medium',
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    fetchContract();
  }, [contractId]);

  const fetchContract = async () => {
    try {
      const res = await api.get(`/helpdesk/my-contracts/${contractId}`);
      if (res.data.success) {
        setContract(res.data.contract);
        setEquipment(res.data.equipment || []);
      }
    } catch (err) {
      setError(isRTL ? 'فشل في تحميل العقد' : 'Failed to load contract');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setError(isRTL ? 'يرجى وصف المشكلة' : 'Please describe the issue');
      return;
    }
    try {
      setSubmitting(true);
      setError('');
      const res = await api.post('/helpdesk/request-service', {
        contract_id: parseInt(contractId),
        equipment_id: formData.equipment_id ? parseInt(formData.equipment_id) : null,
        description: formData.description,
        priority: formData.priority,
      });
      if (res.data.success) {
        setSuccess(res.data.workOrder);
      }
    } catch (err) {
      setError(err.response?.data?.message || (isRTL ? 'فشل في إرسال الطلب' : 'Failed to submit request'));
    } finally {
      setSubmitting(false);
    }
  };

  const L = {
    title: isRTL ? 'طلب خدمة' : 'Request Service',
    subtitle: isRTL ? 'إرسال طلب صيانة أو إصلاح' : 'Submit a maintenance or repair request',
    contract: isRTL ? 'العقد' : 'Contract',
    selectEquipment: isRTL ? 'اختر المعدة (اختياري)' : 'Select Equipment (Optional)',
    noEquipment: isRTL ? 'بدون تحديد معدة' : 'No specific equipment',
    description: isRTL ? 'وصف المشكلة' : 'Describe the Issue',
    descPlaceholder: isRTL ? 'اشرح المشكلة بالتفصيل...' : 'Explain the issue in detail...',
    priority: isRTL ? 'الأولوية' : 'Priority',
    low: isRTL ? 'منخفضة' : 'Low',
    medium: isRTL ? 'متوسطة' : 'Medium',
    high: isRTL ? 'عالية' : 'High',
    critical: isRTL ? 'حرجة' : 'Critical',
    submit: isRTL ? 'إرسال الطلب' : 'Submit Request',
    submitting: isRTL ? 'جاري الإرسال...' : 'Submitting...',
    back: isRTL ? 'رجوع' : 'Back',
    successTitle: isRTL ? 'تم إرسال الطلب بنجاح!' : 'Service Request Submitted!',
    woNumber: isRTL ? 'رقم أمر العمل' : 'Work Order #',
    backToContracts: isRTL ? 'العودة للعقود' : 'Back to Contracts',
  };

  const inputSt = {
    width: '100%', padding: '10px 14px', border: '1px solid #d1d5db',
    borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    backgroundColor: '#fff', color: '#111827',
  };

  const labelSt = {
    display: 'block', marginBottom: '6px', fontSize: '13px',
    fontWeight: '600', color: '#374151',
  };

  if (loading) {
    return (
      <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '40px', textAlign: 'center', color: '#6b7280' }}>
        {isRTL ? 'جاري التحميل...' : 'Loading...'}
      </div>
    );
  }

  if (success) {
    return (
      <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{
          textAlign: 'center', padding: '40px 20px', backgroundColor: '#f0fdf4',
          borderRadius: '12px', border: '1px solid #bbf7d0',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#166534', margin: '0 0 8px' }}>
            {L.successTitle}
          </h2>
          <div style={{ fontSize: '16px', color: '#15803d', fontWeight: '600', marginBottom: '24px' }}>
            {L.woNumber}: {success.wo_number}
          </div>
          <button
            onClick={() => navigate('/contracts')}
            style={{
              padding: '10px 24px', borderRadius: '8px', border: 'none',
              backgroundColor: '#2563eb', color: '#fff', fontSize: '14px',
              fontWeight: '600', cursor: 'pointer',
            }}
          >
            {L.backToContracts}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', maxWidth: '600px' }}>
      <button
        onClick={() => navigate(-1)}
        style={{
          marginBottom: '16px', padding: '6px 12px', border: '1px solid #d1d5db',
          borderRadius: '6px', backgroundColor: '#fff', color: '#374151',
          fontSize: '13px', cursor: 'pointer',
        }}
      >
        ← {L.back}
      </button>

      <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#111827', margin: '0 0 4px' }}>
        🔧 {L.title}
      </h1>
      <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 24px' }}>{L.subtitle}</p>

      {contract && (
        <div style={{
          padding: '12px 16px', backgroundColor: '#eff6ff', borderRadius: '8px',
          border: '1px solid #bfdbfe', marginBottom: '20px', fontSize: '13px', color: '#1e40af',
        }}>
          <strong>{L.contract}:</strong> {contract.contract_number} — {isRTL ? (contract.title_ar || contract.title) : contract.title}
        </div>
      )}

      {error && (
        <div style={{
          padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelSt}>{L.selectEquipment}</label>
          <select value={formData.equipment_id} onChange={(e) => handleChange('equipment_id', e.target.value)} style={inputSt}>
            <option value="">{L.noEquipment}</option>
            {equipment.map(eq => (
              <option key={eq.equipment_id || eq.id} value={eq.equipment_id || eq.id}>
                {isRTL ? (eq.name_ar || eq.name) : eq.name}
                {eq.serial_number ? ` (${eq.serial_number})` : ''}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={labelSt}>{L.priority}</label>
          <select value={formData.priority} onChange={(e) => handleChange('priority', e.target.value)} style={inputSt}>
            <option value="low">{L.low}</option>
            <option value="medium">{L.medium}</option>
            <option value="high">{L.high}</option>
            <option value="critical">{L.critical}</option>
          </select>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={labelSt}>{L.description} *</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder={L.descPlaceholder}
            rows={5}
            style={{ ...inputSt, resize: 'vertical' }}
            required
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
            backgroundColor: submitting ? '#9ca3af' : '#2563eb', color: '#fff',
            fontSize: '15px', fontWeight: '700', cursor: submitting ? 'not-allowed' : 'pointer',
          }}
        >
          {submitting ? L.submitting : L.submit}
        </button>
      </form>
    </div>
  );
};

export default RequestService;
